import * as THREE from 'three';
import { SceneManager } from './renderer/SceneManager';
import { Terrain } from './world/Terrain';
import { CameraManager } from './camera/CameraManager';
import { InputManager } from './input/InputManager';
import { Tank } from './entities/Tank';
import { Infantry } from './entities/Infantry';
import { ProjectileManager } from './entities/ProjectileManager';
import { ParticleSystem } from './fx/ParticleSystem';
import { UIManager } from './ui/UIManager';
import { NetworkClient } from './network/NetworkClient';
import {
  MessageType,
  TANK_HP, INFANTRY_HP, TANK_ENTER_DISTANCE,
  TANK_WEAPONS, INFANTRY_WEAPONS, ALL_WEAPONS,
  computeFireVelocity, isInBounds,
} from '@use/shared';
import type {
  Player, TeamId, PlayerMode, Vec3, WeaponDef,
  GameStateSnapshotMessage, WorldUpdateMessage,
  PlayerJoinedMessage, PlayerLeftMessage,
  ExplosionMessage, TerrainDeformMessage,
  KillMessage, DamageMessage, WeaponFireMessage,
  ProjectileSpawnMessage, ModeSwitchMessage,
  RoundStartMessage, RoundEndMessage,
  PlayerSnapshot, NapalmZoneMessage, AmmoUpdateMessage,
} from '@use/shared';

interface LocalPlayerState {
  id: string;
  name: string;
  teamId: TeamId;
  mode: PlayerMode;
  alive: boolean;
  tankWeapons: WeaponDef[];
  infantryWeapons: WeaponDef[];
  activeWeaponIndex: number;
  weaponCooldowns: Record<string, number>;
  weaponAmmo: Record<string, number>; // weaponId -> remaining ammo (-1 = infinite)
  respawnTimer: number;
  spawnShieldTimer: number;
}

interface RemotePlayerData {
  tank: Tank;
  infantry: Infantry;
  lastSnapshot: PlayerSnapshot | null;
  prevSnapshot: PlayerSnapshot | null;
  interpolationTime: number;
}

const MIN_SHOT_POWER = 0.35;
const MAX_SHOT_POWER = 1.5;
const SHOT_CHARGE_TIME_SEC = 1.2;

export class Game {
  private sceneManager: SceneManager;
  private terrain!: Terrain;
  private camera: CameraManager;
  private input: InputManager;
  private particles!: ParticleSystem;
  private projectiles!: ProjectileManager;
  private ui: UIManager;
  private network: NetworkClient;

  // Local player
  private localState: LocalPlayerState;
  private localTank: Tank | null = null;
  private localInfantry: Infantry | null = null;

  // Remote players
  private remotePlayers: Map<string, RemotePlayerData> = new Map();

  // Game state
  private allPlayers: Map<string, Player> = new Map();
  private redScore = 0;
  private blueScore = 0;
  private roundTimeRemaining = 600;

  // Timing
  private clock = new THREE.Clock();
  private inputSequence = 0;

  // Charged shot state
  private shotCharging = false;
  private shotChargeTime = 0;
  private prevFireDown = false;
  private shotIndicator: THREE.ArrowHelper;
  private localTankFallVelocity = 0;
  private napalmZones: { position: THREE.Vector3; radius: number; endTime: number }[] = [];

  constructor() {
    this.sceneManager = new SceneManager();
    this.camera = new CameraManager();
    this.input = new InputManager();
    this.ui = new UIManager();
    this.network = new NetworkClient();
    this.shotIndicator = new THREE.ArrowHelper(
      new THREE.Vector3(0, 1, 0),
      new THREE.Vector3(),
      2,
      0xffcc00,
      0.7,
      0.45
    );
    this.shotIndicator.visible = false;
    this.sceneManager.scene.add(this.shotIndicator);

    this.localState = {
      id: '',
      name: 'Player',
      teamId: 'red',
      mode: 'tank',
      alive: true,
      tankWeapons: TANK_WEAPONS.slice(0, 5), // default 5
      infantryWeapons: [...INFANTRY_WEAPONS],
      activeWeaponIndex: 0,
      weaponCooldowns: {},
      weaponAmmo: {},
      respawnTimer: 0,
      spawnShieldTimer: 0,
    };
  }

  public async init(): Promise<void> {
    // Create terrain with default seed
    this.terrain = new Terrain(this.sceneManager.scene, 42);
    this.camera.setTerrain(this.terrain);

    // Create particle system
    this.particles = new ParticleSystem(this.sceneManager.scene);

    // Create projectile manager
    this.projectiles = new ProjectileManager(
      this.sceneManager.scene,
      this.terrain,
      this.particles
    );

    // Setup network handlers
    this.setupNetworkHandlers();

    // Setup UI callbacks
    this.ui.onLoadoutConfirmed = (weaponIds) => {
      this.localState.tankWeapons = weaponIds
        .map(id => ALL_WEAPONS.get(id)!)
        .filter(Boolean);
      this.network.send({
        type: MessageType.LOADOUT_SELECT,
        timestamp: Date.now(),
        weaponIds,
      });
    };

    // Try to connect (non-blocking — game works offline for testing)
    try {
      await this.network.connect();
      // Send join
      this.network.send({
        type: MessageType.JOIN_GAME,
        timestamp: Date.now(),
        playerName: `Player${Math.floor(Math.random() * 1000)}`,
      });
    } catch {
      console.log('[Game] Running in offline/test mode');
      this.setupOfflineMode();
    }

    // Start game loop
    this.loop();
  }

  /**
   * Offline mode — spawn a local tank for testing without a server.
   */
  private setupOfflineMode(): void {
    this.localState.id = 'local';
    this.localState.name = 'TestPlayer';
    this.localState.teamId = 'red';
    this.localState.mode = 'tank';

    // Spawn local tank
    const spawnPos: Vec3 = { x: -50, y: 0, z: 0 };
    spawnPos.y = this.terrain.getHeightAt(spawnPos.x, spawnPos.z);
    this.localTank = new Tank(
      this.sceneManager.scene,
      this.localState.id,
      this.localState.teamId,
      spawnPos,
      this.localState.name
    );

    // Spawn local infantry (hidden initially)
    this.localInfantry = new Infantry(
      this.sceneManager.scene,
      this.localState.id,
      this.localState.teamId,
      spawnPos,
      this.localState.name
    );
    this.localInfantry.setVisible(false);

    // Build weapon bar
    this.updateWeaponBar();
    this.ui.updateHealth(TANK_HP, TANK_HP, 'tank');
  }

  private setupNetworkHandlers(): void {
    // Full state snapshot (on join)
    this.network.on(MessageType.GAME_STATE_SNAPSHOT, (msg) => {
      const m = msg as GameStateSnapshotMessage;
      this.localState.id = m.yourPlayerId;
      this.network.playerId = m.yourPlayerId;

      // Rebuild terrain
      this.terrain.regenerate(m.state.terrainSeed);
      for (const crater of m.state.craters) {
        this.terrain.applyCrater(crater);
      }

      // Setup players
      for (const team of m.state.teams) {
        for (const player of team.players) {
          this.allPlayers.set(player.id, player);
          if (player.id === this.localState.id) {
            this.initLocalPlayer(player);
          } else {
            this.addRemotePlayer(player);
          }
        }
      }

      this.roundTimeRemaining = m.state.roundTimeRemaining;
    });

    // World update (20Hz server tick)
    this.network.on(MessageType.WORLD_UPDATE, (msg) => {
      const m = msg as WorldUpdateMessage;
      this.roundTimeRemaining = m.roundTimeRemaining;

      for (const snapshot of m.players) {
        if (snapshot.id === this.localState.id) {
          // Reconcile local state with server
          this.reconcileLocalState(snapshot);
        } else {
          // Update remote player interpolation buffer
          const remote = this.remotePlayers.get(snapshot.id);
          if (remote) {
            remote.prevSnapshot = remote.lastSnapshot;
            remote.lastSnapshot = snapshot;
            remote.interpolationTime = 0;
          }
        }
      }
    });

    // Player joined
    this.network.on(MessageType.PLAYER_JOINED, (msg) => {
      const m = msg as PlayerJoinedMessage;
      this.allPlayers.set(m.player.id, m.player);
      if (m.player.id !== this.localState.id) {
        this.addRemotePlayer(m.player);
      }
      this.ui.showNotification(`${m.player.name} joined ${m.player.teamId} team`, 3000);
    });

    // Player left
    this.network.on(MessageType.PLAYER_LEFT, (msg) => {
      const m = msg as PlayerLeftMessage;
      const player = this.allPlayers.get(m.playerId);
      if (player) {
        this.ui.showNotification(`${player.name} left the game`, 3000);
      }
      this.removeRemotePlayer(m.playerId);
      this.allPlayers.delete(m.playerId);
    });

    // Projectile spawn
    this.network.on(MessageType.PROJECTILE_SPAWN, (msg) => {
      const m = msg as ProjectileSpawnMessage;
      const weaponDef = ALL_WEAPONS.get(m.projectile.weaponId);
      if (weaponDef) {
        this.projectiles.spawn(
          m.projectile.id,
          m.projectile.ownerId,
          weaponDef,
          m.projectile.position,
          m.projectile.velocity
        );
      }
    });

    // Explosion
    this.network.on(MessageType.EXPLOSION, (msg) => {
      const m = msg as ExplosionMessage;
      const pos = new THREE.Vector3(m.position.x, m.position.y, m.position.z);
      // Use weapon-specific explosion effects
      if (m.weaponId === 'nuke') {
        this.particles.emitNukeExplosion(pos, m.radius);
      } else if (m.weaponId === 'shockwave') {
        this.particles.emitShockwaveExplosion(pos, m.radius);
      } else if (m.weaponId === 'napalm') {
        this.particles.emitExplosion(pos, 2, 15);
      } else {
        this.particles.emitExplosion(pos, m.radius);
        this.particles.emitDust(pos);
      }
    });

    // Napalm zone
    this.network.on(MessageType.NAPALM_ZONE, (msg) => {
      const m = msg as NapalmZoneMessage;
      const pos = new THREE.Vector3(m.position.x, m.position.y, m.position.z);
      this.napalmZones.push({
        position: pos,
        radius: m.radius,
        endTime: Date.now() + m.duration * 1000,
      });
    });

    // Ammo update
    this.network.on(MessageType.AMMO_UPDATE, (msg) => {
      const m = msg as AmmoUpdateMessage;
      this.localState.weaponAmmo = m.weaponAmmo;
      this.updateWeaponBar();
    });

    // Terrain deformation (authoritative)
    this.network.on(MessageType.TERRAIN_DEFORM, (msg) => {
      const m = msg as TerrainDeformMessage;
      this.terrain.applyCrater(m.crater);
    });

    // Damage
    this.network.on(MessageType.DAMAGE, (msg) => {
      const m = msg as DamageMessage;
      if (m.targetId === this.localState.id) {
        // Flash screen red briefly
        this.ui.showNotification(`-${m.amount}`, 1000, '#ff4444');
      }
    });

    // Kill
    this.network.on(MessageType.KILL, (msg) => {
      const m = msg as KillMessage;
      const killer = this.allPlayers.get(m.killerId);
      const victim = this.allPlayers.get(m.victimId);
      const weapon = ALL_WEAPONS.get(m.weaponId);
      if (killer && victim) {
        this.ui.addKillEntry(killer.name, victim.name, weapon?.name ?? 'Void', killer.teamId);
      }
    });

    // Mode switch
    this.network.on(MessageType.MODE_SWITCH, (msg) => {
      const m = msg as ModeSwitchMessage;
      if (m.playerId === this.localState.id) {
        this.localState.mode = m.newMode;
        this.applyModeSwitch();
      } else {
        const remote = this.remotePlayers.get(m.playerId);
        if (remote) {
          remote.tank.setVisible(m.newMode === 'tank');
          remote.infantry.setVisible(m.newMode === 'infantry');
        }
      }
    });

    // Round start
    this.network.on(MessageType.ROUND_START, (msg) => {
      const m = msg as RoundStartMessage;
      this.terrain.regenerate(m.terrainSeed);
      this.projectiles.clear();
      this.ui.showNotification('NEW ROUND', 3000, '#ffcc00');
    });

    // Round end
    this.network.on(MessageType.ROUND_END, (msg) => {
      const m = msg as RoundEndMessage;
      const winner = m.winnerTeam;
      this.ui.showNotification(
        winner ? `${winner.toUpperCase()} TEAM WINS!` : 'DRAW!',
        5000,
        winner === 'red' ? '#ff4444' : winner === 'blue' ? '#4488ff' : '#ffffff'
      );
    });
  }

  private initLocalPlayer(player: Player): void {
    this.localState.teamId = player.teamId;
    this.localState.mode = player.mode;
    this.localState.alive = player.alive;
    this.localState.name = player.name;

    // Spawn tank
    const tankPos = player.tankPosition;
    this.localTank = new Tank(
      this.sceneManager.scene,
      player.id,
      player.teamId,
      tankPos,
      player.name
    );

    // Spawn infantry (hidden)
    this.localInfantry = new Infantry(
      this.sceneManager.scene,
      player.id,
      player.teamId,
      player.infantryPosition,
      player.name
    );
    this.localInfantry.setVisible(player.mode === 'infantry');
    this.localTank.setVisible(player.mode === 'tank');

    if (player.tankWeaponLoadout.length > 0) {
      this.localState.tankWeapons = player.tankWeaponLoadout
        .map(id => ALL_WEAPONS.get(id)!)
        .filter(Boolean);
    }

    this.updateWeaponBar();
    this.applyModeSwitch();
  }

  private addRemotePlayer(player: Player): void {
    if (this.remotePlayers.has(player.id)) return;

    const tank = new Tank(
      this.sceneManager.scene,
      player.id,
      player.teamId,
      player.tankPosition,
      player.name
    );

    const infantry = new Infantry(
      this.sceneManager.scene,
      player.id,
      player.teamId,
      player.infantryPosition,
      player.name
    );

    tank.setVisible(player.mode === 'tank');
    infantry.setVisible(player.mode === 'infantry');

    this.remotePlayers.set(player.id, {
      tank,
      infantry,
      lastSnapshot: null,
      prevSnapshot: null,
      interpolationTime: 0,
    });
  }

  private removeRemotePlayer(playerId: string): void {
    const remote = this.remotePlayers.get(playerId);
    if (remote) {
      remote.tank.dispose(this.sceneManager.scene);
      remote.infantry.dispose(this.sceneManager.scene);
      this.remotePlayers.delete(playerId);
    }
  }

  private reconcileLocalState(snapshot: PlayerSnapshot): void {
    // Light reconciliation — trust server for HP, alive, etc.
    // Position is fully client-predicted; only snap on respawn (dead → alive).
    const justRespawned = !this.localState.alive && snapshot.alive;

    if (this.localTank) {
      this.localTank.setHP(snapshot.tankHP);
      this.localTank.alive = snapshot.tankAlive;
      this.localTank.setVisible(snapshot.mode === 'tank' && snapshot.alive && snapshot.tankAlive);
      if (justRespawned) {
        this.localTank.setPosition(snapshot.tankPosition);
      }
    }
    if (this.localInfantry) {
      this.localInfantry.setHP(snapshot.infantryHP);
      this.localInfantry.setVisible(snapshot.mode === 'infantry' && snapshot.alive);
      if (justRespawned) {
        this.localInfantry.setPosition(snapshot.infantryPosition);
      }
    }
    this.localState.alive = snapshot.alive;
    this.localState.spawnShieldTimer = snapshot.spawnShieldTimer;
    if (!snapshot.alive || isInBounds(snapshot.tankPosition.x, snapshot.tankPosition.z)) {
      this.localTankFallVelocity = 0;
    }
  }

  private applyModeSwitch(): void {
    const mode = this.localState.mode;
    if (this.localTank) this.localTank.setVisible(mode === 'tank');
    if (this.localInfantry) this.localInfantry.setVisible(mode === 'infantry');

    this.camera.setMode(mode === 'tank' ? 'orbit' : 'thirdPerson');
    this.ui.setMode(mode);
    this.localState.activeWeaponIndex = 0;
    this.updateWeaponBar();
  }

  private updateWeaponBar(): void {
    const weapons = this.localState.mode === 'tank'
      ? this.localState.tankWeapons
      : this.localState.infantryWeapons;
    this.ui.buildWeaponBar(weapons, this.localState.activeWeaponIndex, this.localState.weaponCooldowns, this.localState.weaponAmmo);
  }

  /**
   * Main game loop.
   */
  private loop = (): void => {
    requestAnimationFrame(this.loop);
    const dt = Math.min(this.clock.getDelta(), 0.05); // cap at 50ms

    // Update input
    this.input.update();

    // Handle camera input
    this.camera.handleMouseInput(this.input.mouseDeltaX, this.input.mouseDeltaY);
    this.camera.handleScroll(this.input.wheelDelta);
    this.input.resetDeltas();

    // Handle gameplay
    this.updateLocalPlayer(dt);
    this.updateRemotePlayers(dt);
    this.updateCooldowns(dt);

    // Handle enter/exit tank
    if (this.input.enterExitTank) {
      this.tryModeSwitch();
    }

    // Handle weapon switch
    if (this.input.switchWeapon >= 0) {
      const maxIdx = this.localState.mode === 'tank'
        ? this.localState.tankWeapons.length - 1
        : this.localState.infantryWeapons.length - 1;
      if (this.input.switchWeapon <= maxIdx) {
        this.localState.activeWeaponIndex = this.input.switchWeapon;
        this.updateWeaponBar();
      }
    }

    // Handle fire / charge
    this.updateFireControl(dt);

    // Handle Tab scoreboard
    this.ui.showScoreboard(this.input.tabHeld);

    // Update projectiles
    this.projectiles.update(dt);

    // Update napalm zone visuals
    const now = Date.now();
    for (let i = this.napalmZones.length - 1; i >= 0; i--) {
      const zone = this.napalmZones[i];
      if (now >= zone.endTime) {
        this.napalmZones.splice(i, 1);
        continue;
      }
      // Emit fire particles in the zone
      this.particles.emitNapalm(zone.position, zone.radius);
    }

    // Update particles
    this.particles.update(dt);

    // Update timer
    this.roundTimeRemaining = Math.max(0, this.roundTimeRemaining - dt);
    this.ui.updateRoundTimer(this.roundTimeRemaining);
    this.ui.updateTeamScores(this.redScore, this.blueScore);

    // Update rosters
    this.updateRosters();

    // Update weapon cooldown overlays
    const weapons = this.localState.mode === 'tank'
      ? this.localState.tankWeapons
      : this.localState.infantryWeapons;
    this.ui.updateWeaponCooldowns(weapons, this.localState.weaponCooldowns);

    // Camera update
    const targetPos = this.getLocalEntityPosition();
    this.camera.update(targetPos, dt);

    // Update health display
    if (this.localState.mode === 'tank' && this.localTank) {
      this.ui.updateHealth(this.localTank.hp, TANK_HP, 'tank');
    } else if (this.localInfantry) {
      this.ui.updateHealth(this.localInfantry.hp, INFANTRY_HP, 'infantry');
    }

    // Render
    this.sceneManager.render(this.camera.camera);

    // Send input to server
    this.sendInput();
  };

  private updateLocalPlayer(dt: number): void {
    if (!this.localState.alive) return;

    if (this.localState.mode === 'tank' && this.localTank) {
      // Tank movement
      this.localTank.move(this.input.moveForward, -this.input.moveRight, dt, this.terrain);

      const tankPos = this.localTank.group.position;
      if (isInBounds(tankPos.x, tankPos.z)) {
        this.localTank.updateTerrainAlignment(this.terrain);
        this.localTankFallVelocity = 0;
      } else {
        this.localTankFallVelocity -= 9.81 * dt;
        tankPos.y += this.localTankFallVelocity * dt;
      }

      // Turret aim follows camera
      this.localTank.turretYaw = (this.camera.getYaw() + Math.PI) - this.localTank.group.rotation.y;
      this.localTank.barrelPitch = Math.max(0, this.camera.getPitch());
      this.localTank.updateTurret();
    } else if (this.localState.mode === 'infantry' && this.localInfantry) {
      // Infantry movement
      this.localInfantry.moveFromInput(
        this.input.moveForward,
        this.input.moveRight,
        this.input.sprint,
        this.input.jump,
        this.camera.getYaw(),
        dt,
        this.terrain
      );
      this.localInfantry.update(this.terrain, dt);
      this.localInfantry.yaw = this.camera.getYaw();
    }
  }

  private updateRemotePlayers(dt: number): void {
    for (const [id, remote] of this.remotePlayers) {
      remote.interpolationTime += dt;

      if (remote.lastSnapshot && remote.prevSnapshot) {
        // Interpolate between two snapshots
        const t = Math.min(1, remote.interpolationTime / 0.05); // 50ms between ticks

        // Tank
        if (remote.lastSnapshot.mode === 'tank') {
          const tank = remote.tank;
          const prev = remote.prevSnapshot;
          const curr = remote.lastSnapshot;
          tank.group.position.lerpVectors(
            new THREE.Vector3(prev.tankPosition.x, prev.tankPosition.y, prev.tankPosition.z),
            new THREE.Vector3(curr.tankPosition.x, curr.tankPosition.y, curr.tankPosition.z),
            t
          );
          tank.turretYaw = prev.turretYaw + (curr.turretYaw - prev.turretYaw) * t;
          tank.barrelPitch = prev.barrelPitch + (curr.barrelPitch - prev.barrelPitch) * t;
          tank.updateTurret();
          tank.setHP(curr.tankHP);
        }

        // Infantry
        if (remote.lastSnapshot.mode === 'infantry') {
          const inf = remote.infantry;
          const prev = remote.prevSnapshot;
          const curr = remote.lastSnapshot;
          inf.group.position.lerpVectors(
            new THREE.Vector3(prev.infantryPosition.x, prev.infantryPosition.y, prev.infantryPosition.z),
            new THREE.Vector3(curr.infantryPosition.x, curr.infantryPosition.y, curr.infantryPosition.z),
            t
          );
          inf.setHP(curr.infantryHP);
        }

        // Visibility
        remote.tank.setVisible(remote.lastSnapshot.mode === 'tank' && remote.lastSnapshot.alive);
        remote.infantry.setVisible(remote.lastSnapshot.mode === 'infantry' && remote.lastSnapshot.alive);
      } else if (remote.lastSnapshot) {
        // No previous snapshot yet — just set position directly
        const snap = remote.lastSnapshot;
        remote.tank.setPosition(snap.tankPosition);
        remote.tank.turretYaw = snap.turretYaw;
        remote.tank.barrelPitch = snap.barrelPitch;
        remote.tank.updateTurret();
        remote.infantry.setPosition(snap.infantryPosition);
      }
    }
  }

  private updateCooldowns(dt: number): void {
    const dtMs = dt * 1000;
    for (const key of Object.keys(this.localState.weaponCooldowns)) {
      this.localState.weaponCooldowns[key] -= dtMs;
      if (this.localState.weaponCooldowns[key] <= 0) {
        delete this.localState.weaponCooldowns[key];
      }
    }
  }

  private tryModeSwitch(): void {
    if (!this.localState.alive) return;

    if (this.localState.mode === 'tank' && this.localTank && this.localInfantry) {
      // Exit tank → infantry
      const tankPos = this.localTank.group.position;
      this.localInfantry.setPosition({
        x: tankPos.x + 3,
        y: this.terrain.getHeightAt(tankPos.x + 3, tankPos.z),
        z: tankPos.z,
      });
      this.localState.mode = 'infantry';
      this.applyModeSwitch();

      this.network.send({
        type: MessageType.MODE_SWITCH,
        timestamp: Date.now(),
        playerId: this.localState.id,
        newMode: 'infantry',
      });
    } else if (this.localState.mode === 'infantry' && this.localTank && this.localInfantry) {
      // Check if near own tank
      const infPos = this.localInfantry.group.position;
      const tankPos = this.localTank.group.position;
      const dist = infPos.distanceTo(tankPos);

      if (dist <= TANK_ENTER_DISTANCE && this.localTank.alive) {
        this.localState.mode = 'tank';
        this.applyModeSwitch();

        this.network.send({
          type: MessageType.MODE_SWITCH,
          timestamp: Date.now(),
          playerId: this.localState.id,
          newMode: 'tank',
        });
      }
    }
  }

  private getCurrentWeapon(): WeaponDef | null {
    const weapons = this.localState.mode === 'tank'
      ? this.localState.tankWeapons
      : this.localState.infantryWeapons;
    return weapons[this.localState.activeWeaponIndex] || null;
  }

  private isChargeableWeapon(weapon: WeaponDef | null): boolean {
    return !!weapon && weapon.projectileSpeed > 0;
  }

  private getCurrentChargePower(): number {
    const t = Math.max(0, Math.min(1, this.shotChargeTime / SHOT_CHARGE_TIME_SEC));
    return MIN_SHOT_POWER + (MAX_SHOT_POWER - MIN_SHOT_POWER) * t;
  }

  private getCurrentChargeNormalized(): number {
    return Math.max(0, Math.min(1, this.shotChargeTime / SHOT_CHARGE_TIME_SEC));
  }

  private updateFireControl(dt: number): void {
    const weapon = this.getCurrentWeapon();
    const fireDown = this.input.fire;

    if (!this.localState.alive || !weapon) {
      this.shotCharging = false;
      this.shotChargeTime = 0;
      this.shotIndicator.visible = false;
      this.ui.updateShotPower(0, false);
      this.prevFireDown = fireDown;
      return;
    }

    if (this.isChargeableWeapon(weapon)) {
      if (fireDown) {
        if (!this.shotCharging) {
          this.shotCharging = true;
          this.shotChargeTime = 0;
        } else {
          this.shotChargeTime = Math.min(SHOT_CHARGE_TIME_SEC, this.shotChargeTime + dt);
        }
        this.updateShotIndicator(this.getCurrentChargePower());
        this.ui.updateShotPower(this.getCurrentChargeNormalized(), true);
      } else {
        if (this.shotCharging) {
          this.tryFire(this.getCurrentChargePower());
        }
        this.shotCharging = false;
        this.shotChargeTime = 0;
        this.shotIndicator.visible = false;
        this.ui.updateShotPower(0, false);
      }
    } else {
      this.shotCharging = false;
      this.shotChargeTime = 0;
      this.shotIndicator.visible = false;
      this.ui.updateShotPower(0, false);

      if (weapon.fireMode === 'auto') {
        if (fireDown) this.tryFire(1);
      } else if (fireDown && !this.prevFireDown) {
        this.tryFire(1);
      }
    }

    this.prevFireDown = fireDown;
  }

  private updateShotIndicator(powerScale: number): void {
    const length = 2 + powerScale * 4;
    this.shotIndicator.visible = true;

    if (this.localState.mode === 'tank' && this.localTank) {
      const tip = this.localTank.getBarrelTip();
      const dir = this.localTank.getFireDirection();
      this.shotIndicator.position.copy(tip);
      this.shotIndicator.setDirection(dir);
      this.shotIndicator.setLength(length, 0.75, 0.5);
      return;
    }

    if (this.localState.mode === 'infantry' && this.localInfantry) {
      const muzzle = this.localInfantry.getMuzzlePosition();
      const dir = this.localInfantry.getAimDirection(this.camera.getPitch());
      this.shotIndicator.position.copy(muzzle);
      this.shotIndicator.setDirection(dir);
      this.shotIndicator.setLength(length, 0.75, 0.5);
      return;
    }

    this.shotIndicator.visible = false;
  }

  private tryFire(powerScale: number = 1): void {
    if (!this.localState.alive) return;

    const weapons = this.localState.mode === 'tank'
      ? this.localState.tankWeapons
      : this.localState.infantryWeapons;

    const weapon = weapons[this.localState.activeWeaponIndex];
    if (!weapon) return;

    // Check cooldown
    if ((this.localState.weaponCooldowns[weapon.id] || 0) > 0) return;

    // Check ammo
    const ammo = this.localState.weaponAmmo[weapon.id] ?? -1;
    if (ammo === 0) return; // out of ammo

    // Consume ammo locally (server is authoritative but this prevents UI lag)
    if (ammo > 0) {
      this.localState.weaponAmmo[weapon.id] = ammo - 1;
      this.updateWeaponBar();
    }

    // Set cooldown
    this.localState.weaponCooldowns[weapon.id] = weapon.cooldownMs;

    if (this.localState.mode === 'tank' && this.localTank) {
      // Tank weapon — fire projectile
      const tip = this.localTank.getBarrelTip();
      const dir = this.localTank.getFireDirection();

      // Shotgun: don't spawn local projectiles — server spawns all pellets
      if (weapon.id !== 'shotgun') {
        const velocity = dir.clone().multiplyScalar(weapon.projectileSpeed * powerScale);
        const projId = `${this.localState.id}_${Date.now()}`;
        this.projectiles.spawn(projId, this.localState.id, weapon,
          { x: tip.x, y: tip.y, z: tip.z },
          { x: velocity.x, y: velocity.y, z: velocity.z }
        );
      }

      // Muzzle flash
      this.particles.emitMuzzleFlash(tip, dir.clone().normalize());

      // Inform server
      this.network.send({
        type: MessageType.WEAPON_FIRE,
        timestamp: Date.now(),
        playerId: this.localState.id,
        weaponId: weapon.id,
        origin: { x: tip.x, y: tip.y, z: tip.z },
        direction: { x: dir.x, y: dir.y, z: dir.z },
        powerScale,
      });
    } else if (this.localState.mode === 'infantry' && this.localInfantry) {
      const muzzle = this.localInfantry.getMuzzlePosition();
      const dir = this.localInfantry.getAimDirection(this.camera.getPitch());

      if (weapon.projectileSpeed > 0) {
        // Projectile weapon (bazooka)
        const velocity = dir.multiplyScalar(weapon.projectileSpeed * powerScale);

        const projId = `${this.localState.id}_${Date.now()}`;
        this.projectiles.spawn(projId, this.localState.id, weapon,
          { x: muzzle.x, y: muzzle.y, z: muzzle.z },
          { x: velocity.x, y: velocity.y, z: velocity.z }
        );

        this.particles.emitMuzzleFlash(muzzle, dir.normalize());
      } else {
        // Hitscan weapon
        this.particles.emitMuzzleFlash(muzzle, dir);

        // TODO: Raycasting for hit detection will be server-authoritative
      }

      // Inform server
      this.network.send({
        type: MessageType.WEAPON_FIRE,
        timestamp: Date.now(),
        playerId: this.localState.id,
        weaponId: weapon.id,
        origin: { x: muzzle.x, y: muzzle.y, z: muzzle.z },
        direction: { x: dir.x, y: dir.y, z: dir.z },
        powerScale,
      });
    }
  }

  private getLocalEntityPosition(): THREE.Vector3 {
    if (this.localState.mode === 'tank' && this.localTank) {
      return this.localTank.group.position;
    } else if (this.localInfantry) {
      return this.localInfantry.group.position;
    }
    return new THREE.Vector3(0, 10, 0);
  }

  private sendInput(): void {
    if (!this.network.isConnected() || !this.localState.id) return;

    this.inputSequence++;
    const aimYaw = this.localState.mode === 'tank'
      ? this.camera.getYaw() + Math.PI
      : this.camera.getYaw();
    this.network.send({
      type: MessageType.PLAYER_INPUT,
      timestamp: Date.now(),
      input: {
        playerId: this.localState.id,
        timestamp: Date.now(),
        moveForward: this.input.moveForward,
        moveRight: this.input.moveRight,
        sprint: this.input.sprint,
        jump: this.input.jump,
        aimYaw,
        aimPitch: this.camera.getPitch(),
        fire: this.input.fire,
        switchWeapon: this.input.switchWeapon,
        enterExitTank: this.input.enterExitTank,
      },
      sequence: this.inputSequence,
    });
  }

  private updateRosters(): void {
    const redPlayers: Player[] = [];
    const bluePlayers: Player[] = [];
    for (const p of this.allPlayers.values()) {
      if (p.teamId === 'red') redPlayers.push(p);
      else bluePlayers.push(p);
    }
    this.ui.updateRosters(redPlayers, bluePlayers);
    if (this.input.tabHeld) {
      this.ui.updateScoreboard(redPlayers, bluePlayers);
    }
  }
}
